/**
 * The rules an IPO application has to satisfy before it is sent.
 *
 * All of it is transcribed from the user platform's own journey
 * (`smest-next/components/IPO/SelectSeries.tsx` and `PaymentSteps.tsx`). The
 * admin is placing the same application on the investor's behalf, so it is
 * validated the same way — a bid the exchange will reject should fail here,
 * where it costs nothing, not at BSE.
 */

import moment from "moment";
import {
  ACTION_CODE_NEW,
  BID_TYPE,
  CATEGORY_4_DISABLED_IPO_IDS,
  DEFAULT_MAX_UNITS,
  DEFAULT_MIN_UNITS,
  HNI_CATEGORY,
  PAY_TYPE,
  PERMITTED_UPI_HANDLES,
  RETAIL_CATEGORY,
  RETAIL_MAX_INVESTMENT,
  SPECIAL_FACE_VALUE,
  SPECIAL_FACE_VALUE_IPO_ID,
  SPECIAL_FACE_VALUE_SERIES,
  SPECIAL_SERIES_MAX_UNITS,
  SPECIAL_SERIES_MIN_UNITS,
  UPI_BANK_NAME,
  UPI_ID_PATTERN,
  UPI_LOCATION,
} from "./constants";
import type {
  IPODetailsQuote,
  IPODetailsSecurityMaster,
  IPOInvestorDetails,
  IPOOrderCreatePayload,
  IPOOrderFormState,
} from "./interfaces";

/**
 * Whether an issue is still open to applications.
 *
 * The exact rule the user platform shows IPOs by, in both
 * `pages/explore-bonds.tsx` (the IPO Arcade grid) and
 * `components/IPO/SelectIPOs.tsx` (the "CURRENT IPOs" list): the issue is open
 * while now is before 17:00 on its `issue_close_date`.
 *
 * Note it deliberately tests the CLOSE date only — an issue whose open date has
 * not arrived yet still counts as current there, so it does here too rather
 * than diverging.
 */
export const isIpoOpenForApplication = (
  issueCloseDate?: string | null
): boolean => {
  if (!issueCloseDate) return false;
  const closeDate = moment(String(issueCloseDate).slice(0, 10), "YYYY-MM-DD");
  if (!closeDate.isValid()) return false;
  return moment().isBefore(
    closeDate.set({ hour: 17, minute: 0, second: 0, millisecond: 0 })
  );
};

/** Series values arrive as "Series 3", "3", or 3 depending on the row. */
export const parseSeriesNumber = (
  value: string | number | undefined | null
): number | null => {
  if (value == null) return null;
  const match = String(value).match(/\d+/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
};

export const isSpecialFaceValueIpo = (ipoUuid?: string | null) =>
  String(ipoUuid || "").toLowerCase() === SPECIAL_FACE_VALUE_IPO_ID.toLowerCase();

export const isCategory4Disabled = (ipoUuid?: string | null) =>
  CATEGORY_4_DISABLED_IPO_IDS.some(
    (id) => id.toLowerCase() === String(ipoUuid || "").toLowerCase()
  );

/**
 * A series' face value. One IPO overrides it for series 3; every other issue
 * uses the security master's own figure.
 */
export const getSeriesFaceValue = (
  seriesValue: string | number | undefined,
  defaultFaceValue: number,
  ipoUuid?: string | null
): number =>
  isSpecialFaceValueIpo(ipoUuid) &&
  parseSeriesNumber(seriesValue) === SPECIAL_FACE_VALUE_SERIES
    ? SPECIAL_FACE_VALUE
    : defaultFaceValue;

export const getSeriesUnitBounds = (
  seriesValue: string | number | undefined,
  ipoUuid?: string | null
): { min: number; max: number } =>
  isSpecialFaceValueIpo(ipoUuid) &&
  parseSeriesNumber(seriesValue) === SPECIAL_FACE_VALUE_SERIES
    ? { min: SPECIAL_SERIES_MIN_UNITS, max: SPECIAL_SERIES_MAX_UNITS }
    : { min: DEFAULT_MIN_UNITS, max: DEFAULT_MAX_UNITS };

/** Retail at or below ₹2,00,000; HNI above it. */
export const categoryForInvestment = (totalInvestment: number): number =>
  totalInvestment > RETAIL_MAX_INVESTMENT ? HNI_CATEGORY : RETAIL_CATEGORY;

export const compareSeries = (
  left: IPODetailsQuote,
  right: IPODetailsQuote
): number => {
  const leftValue = parseSeriesNumber(left.series_frontend);
  const rightValue = parseSeriesNumber(right.series_frontend);
  if (leftValue !== null && rightValue !== null) return leftValue - rightValue;
  if (leftValue !== null) return -1;
  if (rightValue !== null) return 1;
  return String(left.series_frontend || "").localeCompare(
    String(right.series_frontend || "")
  );
};

/**
 * Validates a UPI id in two steps, as the user platform does: the shape, then
 * the handle against the sponsor bank's permitted list.
 */
export const validateUpiId = (value: string): string => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "Enter the investor's UPI ID.";
  if (!UPI_ID_PATTERN.test(trimmed)) return "Enter a valid UPI ID.";
  const handle = `@${trimmed.split("@")[1]}`.toLowerCase();
  if (PERMITTED_UPI_HANDLES.indexOf(handle) < 0) {
    return `${handle} is not a permitted UPI handle for this issue.`;
  }
  return "";
};

/** BSE's order reference — ten uppercase alphanumerics, generated per bid. */
export const generateOrderNumber = (length = 10): string => {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let code = "";
  for (let index = 0; index < length; index += 1) {
    code += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return code;
};

/**
 * The issuer's name, whichever shape the endpoint used.
 *
 * `/ipo/details/` expands `issuer_of_security` into an object; the security
 * master list leaves it as a uuid string. Returns "" when it cannot be read,
 * so callers fall back rather than render "undefined".
 */
export const getIssuerName = (
  securityMaster?: IPODetailsSecurityMaster | null
): string => {
  const issuer = securityMaster ? securityMaster.issuer_of_security : null;
  if (!issuer || typeof issuer === "string") return "";
  return (issuer.issuerName && issuer.issuerName.name) || "";
};

export const getDefaultDemat = (investor?: IPOInvestorDetails | null) => {
  const accounts = (investor && investor.dematDetails) || [];
  return accounts.find((account) => account.is_default) || accounts[0] || null;
};

/**
 * Splits a stored demat id into what the payload needs.
 *
 * NSDL ids are sixteen characters, "IN" + six digits of DP id + eight of
 * beneficiary id; the payload wants `dpid` as "IN" + those six, and
 * `clientbenfid` as the last eight. CDSL has no DP id — the payload sends "0"
 * and the whole sixteen-digit id as `clientbenfid`.
 */
export const splitDematId = (
  dpType?: string | null,
  dematId?: string | null
): { depository: string; dpId6: string; dpId8: string; clientId: string } => {
  const depository = String(dpType || "NSDL").toUpperCase();
  const id = String(dematId || "").trim();
  if (depository === "NSDL") {
    const digits = id.toUpperCase().indexOf("IN") === 0 ? id.slice(2) : id;
    return {
      depository,
      dpId6: digits.slice(0, 6),
      dpId8: digits.slice(6, 14),
      clientId: "",
    };
  }
  return { depository, dpId6: "", dpId8: "", clientId: id };
};

export interface IPOOrderValidationContext {
  investorUuid: string;
  securityMaster: IPODetailsSecurityMaster | null;
  quote: IPODetailsQuote | null;
  form: IPOOrderFormState;
}

export interface IPOOrderValidationResult {
  problems: string[];
  units: number;
  faceValue: number;
  totalInvestment: number;
  derivedCategory: number | null;
  bounds: { min: number; max: number };
}

/**
 * Everything that would stop this application, in one pass.
 *
 * Returned rather than thrown so the screen can show the figures it derived
 * alongside the reasons — an admin correcting an order needs to see both.
 */
export const validateIPOOrder = (
  context: IPOOrderValidationContext
): IPOOrderValidationResult => {
  const { investorUuid, securityMaster, quote, form } = context;
  const problems: string[] = [];

  const ipoUuid = securityMaster ? securityMaster.uuid : null;
  const faceValue = quote
    ? getSeriesFaceValue(
        quote.series_frontend,
        Number(securityMaster ? securityMaster.face_value : 0) || 0,
        ipoUuid
      )
    : 0;
  const bounds = quote
    ? getSeriesUnitBounds(quote.series_frontend, ipoUuid)
    : { min: DEFAULT_MIN_UNITS, max: DEFAULT_MAX_UNITS };

  const units = Number(form.units);
  const totalInvestment =
    Number.isFinite(units) && units > 0 ? units * faceValue : 0;
  const derivedCategory = totalInvestment ? categoryForInvestment(totalInvestment) : null;

  if (!investorUuid) problems.push("Select the investor this order is for.");
  if (!securityMaster) problems.push("Select an IPO.");
  if (!quote) problems.push("Select a series.");

  // Re-checked at submit, not just when the list was built: a form left open
  // across 17:00 on the close date would otherwise post into a shut issue.
  if (
    securityMaster &&
    !isIpoOpenForApplication(securityMaster.issue_close_date)
  ) {
    problems.push(
      "This issue has closed — applications are only accepted until 5:00 pm on the close date."
    );
  }

  if (quote && !faceValue) {
    problems.push("This issue did not return a face value, so the amount cannot be computed.");
  }

  if (!Number.isInteger(units) || units <= 0) {
    problems.push("Enter the number of bonds as a whole number.");
  } else if (units < bounds.min || units > bounds.max) {
    problems.push(
      `This series takes between ${bounds.min} and ${bounds.max} bonds.`
    );
  }

  // The category on the quote must match the size of the application, or the
  // exchange books it under the wrong reservation bucket.
  if (quote && derivedCategory != null) {
    // `category` is the 3/4 bucket the user platform filters its tabs on.
    // `category_subcode` is a different BSE code and is what the payload
    // sends — comparing against that instead would silently pass.
    const quoteCategory = parseSeriesNumber(quote.category);
    if (quoteCategory != null && quoteCategory !== derivedCategory) {
      problems.push(
        derivedCategory === HNI_CATEGORY
          ? `Above ₹${RETAIL_MAX_INVESTMENT.toLocaleString("en-IN")} this is an HNI application — pick a category ${HNI_CATEGORY} series or reduce the quantity.`
          : `At or below ₹${RETAIL_MAX_INVESTMENT.toLocaleString("en-IN")} this is a Retail application — pick a category ${RETAIL_CATEGORY} series or increase the quantity.`
      );
    }
    if (derivedCategory === RETAIL_CATEGORY && isCategory4Disabled(ipoUuid)) {
      problems.push(
        "Retail (category 4) is closed on this issue, so the application has to be sized as HNI."
      );
    }
  }

  if (!form.applicantName.trim()) problems.push("Enter the applicant name.");
  if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(form.panNumber.trim().toUpperCase())) {
    problems.push("Enter a valid PAN.");
  }

  if (form.depository === "NSDL") {
    if (!/^\d{6}$/.test(form.dpId6)) problems.push("The NSDL DP ID must be six digits after IN.");
    if (!/^\d{8}$/.test(form.dpId8)) problems.push("The NSDL beneficiary ID must be eight digits.");
  } else if (form.depository === "CDSL") {
    if (!/^\d{16}$/.test(form.clientId)) problems.push("The CDSL client ID must be sixteen digits.");
  } else {
    problems.push("Select a depository.");
  }

  const upiProblem = validateUpiId(form.upiId);
  if (upiProblem) problems.push(upiProblem);

  return { problems, units, faceValue, totalInvestment, derivedCategory, bounds };
};

/**
 * Builds the `/ipo/ipo_ordercreate/` body.
 *
 * Field for field the payload the user platform sends. `chequereceivedflag` is
 * "N" on creation — the money has not arrived; the UPI mandate is only raised
 * once the exchange accepts the bid.
 */
export const buildIPOOrderPayload = (args: {
  investor: IPOInvestorDetails;
  securityMaster: IPODetailsSecurityMaster;
  quote: IPODetailsQuote;
  form: IPOOrderFormState;
  units: number;
  faceValue: number;
  totalInvestment: number;
}): IPOOrderCreatePayload => {
  const { investor, securityMaster, quote, form, units, faceValue, totalInvestment } = args;
  const isNsdl = form.depository === "NSDL";

  return {
    user_id: investor.uuid,
    scripid: securityMaster.scrip_id,
    applicationno: "",
    category: quote.category_subcode,
    applicantname: form.applicantName.trim(),
    depository: form.depository,
    dpid: isNsdl ? `IN${form.dpId6}` : "0",
    clientbenfid: isNsdl ? form.dpId8 : form.clientId,
    chequereceivedflag: "N",
    chequeamount: totalInvestment,
    totalqty: units,
    panno: form.panNumber.trim().toUpperCase(),
    bankname: UPI_BANK_NAME,
    location: UPI_LOCATION,
    accountnumber_upiid: form.upiId.trim(),
    ifsccode: "",
    bankreferencechequeno: "0",
    bidtype: BID_TYPE,
    paytype: PAY_TYPE,
    actioncode: ACTION_CODE_NEW,
    bids: [
      {
        bidid: "0",
        quantity: units,
        rate: faceValue,
        cuttoffflag: "0",
        series: quote.series_backend,
        orderno: generateOrderNumber(10),
      },
    ],
    issuerName: getIssuerName(securityMaster) || undefined,
    smest_Id: investor.smestID,
    mobile: investor.mobileNumber,
    email: investor.emailId,
    ipo_quote_tenure: quote.tenure,
    ipo_quote_uuid: quote.uuid,
    quote_yield: quote.quote_yield,
  };
};

/**
 * Reads the exchange's verdict out of a 200.
 *
 * The endpoint answers 200 with the bid's own error inside it, so the HTTP
 * status is never the verdict — `errorcode` on the bid row is.
 */
export const readOrderOutcome = (
  response: any
): { accepted: boolean; message: string; bidId: string; applicationNo: string } => {
  const bid = response && response.bids && response.bids[0];
  const bidError = bid && bid.errorcode && String(bid.errorcode) !== "0";
  const statusCode = response && response.statuscode;
  const statusFailed =
    statusCode != null && String(statusCode) !== "0" && String(statusCode) !== "";
  const message =
    (bid && bid.message) ||
    (response && response.statusmessage) ||
    "The exchange returned no message for this application.";
  return {
    accepted: !bidError && !statusFailed,
    message,
    bidId: (bid && bid.bidid) || "",
    applicationNo: (response && response.applicationno) || "",
  };
};
